import { useState, useRef, useEffect, useId } from 'react'
import { useTranslation } from 'react-i18next'
import { useApiIsLoaded } from '@vis.gl/react-google-maps'
import { usePlaceAutocomplete } from '../hooks/usePlaceAutocomplete'
import { extractAddressComponents, ResolvedPlaceDetails } from '../utils/extractAddressComponents'

export interface AddressAutocompleteInputProps {
  /**
   * Initial text shown in the search field — typically the existing formattedAddress
   * when editing an apartment that already has geocoding data.
   */
  initialValue?: string
  /** Called when the user selects a suggestion and place details have been resolved. */
  onPlaceSelected: (place: ResolvedPlaceDetails) => void
  /**
   * Called when the user clears the search input after a prior selection.
   * Signals the consumer to clear geocoding fields from form state.
   */
  onCleared: () => void
  /** Disables the input — e.g. while a save is in progress. */
  disabled?: boolean
  /** Placeholder text for the search input. */
  placeholder?: string
}

/**
 * Address search field with Google Places autocomplete dropdown.
 * Renders a WAI-ARIA combobox with a listbox dropdown of place suggestions.
 * Calls onPlaceSelected with fully resolved address data when the user picks a suggestion.
 */
export function AddressAutocompleteInput({
  initialValue = '',
  onPlaceSelected,
  onCleared,
  disabled = false,
  placeholder,
}: AddressAutocompleteInputProps) {
  const { t } = useTranslation(['forms', 'errors'])
  const apiLoaded = useApiIsLoaded()
  const listboxId = useId()

  const [inputValue, setInputValue] = useState(initialValue)
  const [isOpen, setIsOpen] = useState(false)
  const [resolving, setResolving] = useState(false)
  const [resolveError, setResolveError] = useState(false)
  // Track whether the current input value was set by a prior autocomplete selection
  const hasSelectionRef = useRef(!!initialValue)

  const { suggestions, loading, fetchSuggestions, clearSuggestions } = usePlaceAutocomplete()

  // Sync initialValue changes (e.g. when apartment data loads asynchronously)
  useEffect(() => {
    setInputValue(initialValue)
    hasSelectionRef.current = !!initialValue
  }, [initialValue])

  // Open dropdown whenever there are suggestions
  useEffect(() => {
    setIsOpen(suggestions.length > 0)
  }, [suggestions])

  const handleInputChange = (value: string) => {
    setInputValue(value)
    setResolveError(false)

    if (!value) {
      clearSuggestions()
      setIsOpen(false)
      if (hasSelectionRef.current) {
        hasSelectionRef.current = false
        onCleared()
      }
      return
    }

    // If user starts typing after a prior selection, treat as cleared
    if (hasSelectionRef.current) {
      hasSelectionRef.current = false
      onCleared()
    }

    fetchSuggestions(value)
  }

  const handleSelect = async (prediction: google.maps.places.PlacePrediction, displayText: string) => {
    setInputValue(displayText)
    clearSuggestions()
    setIsOpen(false)
    setResolving(true)
    setResolveError(false)

    try {
      const details = await extractAddressComponents(prediction)
      hasSelectionRef.current = true
      onPlaceSelected(details)
    } catch {
      setResolveError(true)
    } finally {
      setResolving(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      clearSuggestions()
      setIsOpen(false)
    }
  }

  const isDisabled = disabled || !apiLoaded || resolving

  return (
    <div className="relative">
      <input
        type="text"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-label={t('forms:addressSearch')}
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
        placeholder={placeholder ?? t('forms:addressSearchPlaceholder')}
        className="input w-full"
        autoComplete="off"
      />

      {/* Loading / resolving indicator */}
      {(loading || resolving) && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <span
            className="inline-block w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          />
        </div>
      )}

      {/* Resolve error */}
      {resolveError && (
        <p className="text-red-500 text-sm mt-1">{t('errors:geocodingFailed')}</p>
      )}

      {/* Dropdown */}
      {isOpen && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label={t('forms:addressSearch')}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.length === 0 && !loading ? (
            <li className="px-4 py-3 text-sm text-gray-500">{t('forms:noAddressResults')}</li>
          ) : (
            suggestions.map((suggestion) => (
              <li
                key={suggestion.placeId}
                role="option"
                aria-selected={false}
                onMouseDown={(e) => {
                  // Prevent input blur before click registers
                  e.preventDefault()
                  void handleSelect(suggestion.prediction, suggestion.displayText)
                }}
                className="px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                {suggestion.displayText}
              </li>
            ))
          )}
        </ul>
      )}

      {/* API not yet loaded */}
      {!apiLoaded && (
        <p className="text-gray-400 text-xs mt-1">{t('errors:placesUnavailable')}</p>
      )}
    </div>
  )
}
